/**
 * MeasurementComparison.jsx — Comparateur de mensurations entre 2 dates + moteur
 * d'analyse professionnelle (interprétation des variations croisées : perte de gras,
 * gain musculaire, recomposition corporelle, prise de masse, plateau...).
 */
import React, { useMemo, useState } from 'react';
import {
  ArrowRight, TrendingDown, TrendingUp, Minus, Sparkles, Flame, Dumbbell,
  AlertTriangle, Info, BarChart3,
} from 'lucide-react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';

// Zones affichées dans le tableau de comparaison, dans l'ordre visuel du corps.
// lowerBetter: true = une baisse est une amélioration (zones adipeuses) ; false = une
// hausse est une amélioration (zones musculaires) ; null = neutre (poids, contextuel).
const ZONES = [
  { key: 'chest', fr: 'Poitrine', en: 'Chest', unit: 'cm', lowerBetter: false, group: 'muscle' },
  { key: 'waist', fr: 'Taille', en: 'Waist', unit: 'cm', lowerBetter: true, group: 'fat' },
  { key: 'hips', fr: 'Hanches', en: 'Hips', unit: 'cm', lowerBetter: true, group: 'fat' },
  { key: 'biceps', fr: 'Biceps', en: 'Biceps', unit: 'cm', lowerBetter: false, group: 'muscle' },
  { key: 'thigh', fr: 'Cuisse', en: 'Thigh', unit: 'cm', lowerBetter: false, group: 'muscle' },
  { key: 'calf', fr: 'Mollet', en: 'Calf', unit: 'cm', lowerBetter: false, group: 'muscle' },
  { key: 'body_fat', fr: '% Masse grasse', en: 'Body fat %', unit: '%', lowerBetter: true, group: 'fat' },
  { key: 'weight', fr: 'Poids', en: 'Weight', unit: 'kg', lowerBetter: null, group: 'neutral' },
];

const SIG_CM = 0.4; // Seuil (cm ou %) au-delà duquel une variation est considérée significative
const fmtDate = (iso) => {
  try { return new Date(iso + 'T12:00:00').toLocaleDateString('fr-CH', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

function computeDeltas(entryA, entryB) {
  const out = {};
  ZONES.forEach((z) => {
    const a = entryA?.[z.key];
    const b = entryB?.[z.key];
    if (a == null || b == null) return;
    const abs = +(b - a).toFixed(1);
    const pct = a !== 0 ? +((abs / a) * 100).toFixed(1) : null;
    out[z.key] = { a, b, abs, pct };
  });
  return out;
}

function avgAbs(deltas, keys) {
  const vals = keys.map((k) => deltas[k]?.abs).filter((v) => v != null);
  if (!vals.length) return null;
  return vals.reduce((s, v) => s + v, 0) / vals.length;
}

/** Moteur d'analyse professionnelle : verdict global + détail rassurant zone par zone. */
function analyze(deltas, en) {
  const fatKeys = ZONES.filter((z) => z.group === 'fat' && z.key !== 'body_fat').map((z) => z.key);
  const muscleKeys = ZONES.filter((z) => z.group === 'muscle').map((z) => z.key);
  const fatAvg = avgAbs(deltas, fatKeys); // négatif = perte de gras
  const muscleAvg = avgAbs(deltas, muscleKeys); // positif = gain musculaire
  const weightDelta = deltas.weight?.abs ?? null;
  const bodyFatDelta = deltas.body_fat?.abs ?? null;
  const anyFatLoss = fatKeys.some((k) => deltas[k]?.abs <= -SIG_CM);

  const fatLoss = fatAvg != null && fatAvg <= -SIG_CM;
  const fatGain = fatAvg != null && fatAvg >= SIG_CM;
  const fatStable = fatAvg != null && Math.abs(fatAvg) < SIG_CM;
  const muscleGain = muscleAvg != null && muscleAvg >= SIG_CM;
  const muscleLoss = muscleAvg != null && muscleAvg <= -SIG_CM;
  const muscleStable = muscleAvg != null && Math.abs(muscleAvg) < SIG_CM;

  const insights = [];

  // 1. VERDICT GLOBAL — la carte la plus importante, phrases courtes et rassurantes
  if (fatLoss && muscleGain) {
    insights.push({
      emphasis: true, icon: Sparkles, color: '#F59E0B', tone: 'success',
      title: en ? 'Body recomposition — excellent!' : 'Bravo, recomposition corporelle !',
      desc: en
        ? `You lost fat (waist/abdomen/hips) and gained size on other zones (arms, thighs...). Don't worry about that — it's very likely muscle, not fat, since your fat zones shrank at the same time. This is the best possible result.`
        : `Vous avez perdu du gras (ventre/abdomen/hanches) ET pris quelques cm ailleurs (bras, cuisses...). Ne vous inquiétez pas : c'est très probablement du muscle, pas du gras, puisque vos zones adipeuses ont diminué en même temps. C'est le meilleur résultat possible !`,
    });
  } else if (fatLoss && muscleStable) {
    insights.push({
      emphasis: true, icon: Flame, color: '#10B981', tone: 'success',
      title: en ? 'Clean fat loss' : 'Bravo, perte de gras nette !',
      desc: en
        ? `Your fat zones shrank while your muscle mass stayed stable. You're losing fat without losing muscle — exactly what you want.`
        : `Vos zones adipeuses ont diminué alors que votre masse musculaire est restée stable. Vous perdez du gras sans perdre de muscle — c'est exactement ce qu'il faut.`,
    });
  } else if (fatLoss && muscleLoss) {
    insights.push({
      emphasis: true, icon: AlertTriangle, color: '#FBBF24', tone: 'warning',
      title: en ? 'Overall weight loss — watch your muscle' : 'Perte de poids globale — attention au muscle',
      desc: en
        ? `You lost both fat and muscle. If you only want fat loss, eat more protein and keep training with weights to protect your muscle.`
        : `Vous avez perdu à la fois du gras et du muscle. Si vous voulez perdre uniquement du gras, mangez plus de protéines et continuez la musculation pour protéger votre masse musculaire.`,
    });
  } else if (fatGain && muscleGain) {
    insights.push({
      emphasis: true, icon: Dumbbell, color: '#3B82F6', tone: 'info',
      title: en ? 'Mass-gain phase' : 'Phase de prise de masse',
      desc: en
        ? `You're gaining size everywhere, muscle and a bit of fat. Normal in a mass-gain phase — just keep an eye on your waist so it stays a "clean" gain.`
        : `Vous prenez du volume partout, du muscle et un peu de gras. C'est normal en phase de prise de masse — surveillez juste votre tour de taille pour que ça reste une prise "propre".`,
    });
  } else if (fatGain && (muscleStable || muscleLoss)) {
    insights.push({
      emphasis: true, icon: AlertTriangle, color: '#fb7185', tone: 'warning',
      title: en ? 'Fat gain without muscle progress' : 'Prise de gras sans progrès musculaire',
      desc: en
        ? `Your fat zones grew without a matching muscle gain. Worth checking your calories and training intensity.`
        : `Vos zones adipeuses ont augmenté sans gain musculaire en face. Ça vaut le coup de revoir vos calories et l'intensité de vos entraînements.`,
    });
  } else if (fatStable && muscleGain) {
    insights.push({
      emphasis: true, icon: Dumbbell, color: '#10B981', tone: 'success',
      title: en ? 'Pure muscle gain' : 'Bravo, gain musculaire pur !',
      desc: en
        ? `Your muscle zones grew while your fat zones stayed stable — a very efficient muscle-building phase.`
        : `Vos zones musculaires ont progressé alors que vos zones adipeuses sont restées stables — une phase de construction musculaire très efficace.`,
    });
  } else if (fatStable && muscleStable) {
    insights.push({
      emphasis: true, icon: Minus, color: '#a1a1aa', tone: 'info',
      title: en ? 'Stable — plateau' : 'Mensurations stables — plateau',
      desc: en
        ? `No real change between these 2 dates. If this lasts several weeks, adjust your calories or training volume to relaunch progress.`
        : `Pas de changement notable entre ces 2 dates. Si ça dure depuis plusieurs semaines, ajustez vos calories ou votre volume d'entraînement pour relancer la progression.`,
    });
  }

  // 2. DÉTAIL RASSURANT ZONE PAR ZONE — phrases courtes, une par zone significative
  ZONES.forEach((z) => {
    if (z.group === 'neutral') return; // le poids est traité séparément plus bas
    const d = deltas[z.key];
    if (!d || Math.abs(d.abs) < SIG_CM) return;
    const label = en ? z.en : z.fr;
    const sign = d.abs > 0 ? '+' : '';

    if (z.group === 'fat') {
      if (d.abs < 0) {
        insights.push({
          icon: Flame, color: '#34D399', tone: 'success',
          title: `${label} : ${d.abs}${z.unit}`,
          desc: en ? `Great, a decrease here is a clear sign of fat loss in this area.` : `Bravo, une baisse ici montre clairement une perte de gras à cet endroit.`,
        });
      } else {
        insights.push({
          icon: AlertTriangle, color: '#fb7185', tone: 'warning',
          title: `${label} : ${sign}${d.abs}${z.unit}`,
          desc: en ? `Worth watching — a slight fat gain is possible in this area.` : `À surveiller : une légère prise de graisse est possible à cet endroit.`,
        });
      }
    } else { // muscle
      if (d.abs > 0) {
        if (anyFatLoss) {
          insights.push({
            icon: Dumbbell, color: '#34D399', tone: 'success',
            title: `${label} : ${sign}${d.abs}${z.unit}`,
            desc: en ? `Don't worry about this increase — since your fat zones shrank at the same time, this is very likely muscle, not fat.` : `Ne vous affolez pas pour cette hausse : comme vos zones adipeuses ont diminué en même temps, c'est très probablement du muscle, pas du gras.`,
          });
        } else {
          insights.push({
            icon: Dumbbell, color: '#60A5FA', tone: 'info',
            title: `${label} : ${sign}${d.abs}${z.unit}`,
            desc: en ? `Could be muscle or a bit of fat depending on your current diet — worth confirming at your next measurement.` : `Peut être du muscle ou un peu de gras selon votre alimentation actuelle — à confirmer à la prochaine mesure.`,
          });
        }
      } else {
        insights.push({
          icon: TrendingDown, color: '#fb7185', tone: 'warning',
          title: `${label} : ${d.abs}${z.unit}`,
          desc: en ? `If you're in an overall weight-loss phase, a small muscle loss can happen here — increase protein and keep training with weights.` : `Si vous êtes en perte de poids globale, une petite perte musculaire est possible ici : augmentez vos protéines et maintenez la musculation.`,
        });
      }
    }
  });

  // Note complémentaire sur le % de masse grasse si disponible
  if (bodyFatDelta != null && Math.abs(bodyFatDelta) >= 0.3) {
    insights.push({
      icon: bodyFatDelta < 0 ? TrendingDown : TrendingUp,
      color: bodyFatDelta < 0 ? '#10B981' : '#fb7185', tone: bodyFatDelta < 0 ? 'success' : 'warning',
      title: en ? `Body fat ${bodyFatDelta < 0 ? 'decreased' : 'increased'} by ${Math.abs(bodyFatDelta)}%` : `Masse grasse ${bodyFatDelta < 0 ? 'en baisse' : 'en hausse'} de ${Math.abs(bodyFatDelta)}%`,
      desc: en
        ? `This confirms the trend seen on your circumference measurements.`
        : `Cela confirme la tendance observée sur vos tours de mesure.`,
    });
  }

  // Note sur le poids si aucune autre variation notable (poids seul disponible)
  if (!insights.length && weightDelta != null) {
    insights.push({
      emphasis: true, icon: weightDelta < 0 ? TrendingDown : weightDelta > 0 ? TrendingUp : Minus,
      color: '#3B82F6', tone: 'info',
      title: en ? 'Weight change' : 'Évolution du poids',
      desc: en
        ? `Weight ${weightDelta === 0 ? 'unchanged' : weightDelta < 0 ? `down ${Math.abs(weightDelta)} kg` : `up ${weightDelta} kg`}. Add more body measurements next time for a full recomposition analysis.`
        : `Poids ${weightDelta === 0 ? 'inchangé' : weightDelta < 0 ? `en baisse de ${Math.abs(weightDelta)} kg` : `en hausse de ${weightDelta} kg`}. Ajoutez plus de mensurations la prochaine fois pour une analyse complète de recomposition.`,
    });
  }

  if (!insights.length) {
    insights.push({
      emphasis: true, icon: Info, color: '#a1a1aa', tone: 'info',
      title: en ? 'Not enough data' : 'Données insuffisantes',
      desc: en
        ? 'Add more measurement zones on both dates to get a professional analysis.'
        : 'Ajoutez plus de zones de mesure sur ces 2 dates pour obtenir une analyse professionnelle.',
    });
  }

  return insights;
}

const TONE_BG = {
  success: 'bg-emerald-500/10 border-emerald-500/30',
  warning: 'bg-amber-500/10 border-amber-500/30',
  info: 'bg-blue-500/10 border-blue-500/30',
};

export const MeasurementComparison = ({ entries, en }) => {
  // Dates disponibles (ordre chronologique, déjà trié par le backend)
  const dates = useMemo(() => entries.map((e) => e.date), [entries]);
  const [dateA, setDateA] = useState(dates[0]);
  const [dateB, setDateB] = useState(dates[dates.length - 1]);

  // Si la liste d'entrées change (nouvelle mesure ajoutée), on recale la borne "actuelle"
  // sur la plus récente pour toujours montrer la progression la plus fraîche.
  const latestDate = dates[dates.length - 1];
  const effDateB = dates.includes(dateB) ? dateB : latestDate;
  const effDateA = dates.includes(dateA) ? dateA : dates[0];

  // Peu importe dans quel sélecteur l'utilisateur place chaque date, l'analyse utilise
  // TOUJOURS la date chronologiquement la plus ancienne comme référence "Avant" et la plus
  // récente comme "Après" — sinon un verdict inversé (silencieux) apparaît si l'utilisateur
  // choisit la date récente dans le sélecteur de gauche. Des étiquettes dynamiques "Avant"/
  // "Après" sous chaque sélecteur rendent ce comportement visible et sans ambiguïté.
  const selAIsEarlier = dates.indexOf(effDateA) <= dates.indexOf(effDateB);
  const beforeDate = selAIsEarlier ? effDateA : effDateB;
  const afterDate = selAIsEarlier ? effDateB : effDateA;

  const entryA = entries.find((e) => e.date === beforeDate);
  const entryB = entries.find((e) => e.date === afterDate);
  const deltas = useMemo(() => computeDeltas(entryA, entryB), [entryA, entryB]);
  const insights = useMemo(() => analyze(deltas, en), [deltas, en]);
  const rows = ZONES.filter((z) => deltas[z.key]);

  return (
    <div className="mt-5 rounded-xl bg-zinc-800/40 border border-white/10 p-4" data-testid="measurement-comparison">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-4 h-4 text-primary" />
        <h4 className="font-bold text-white text-sm">{en ? 'Progress comparison' : 'Comparaison de progression'}</h4>
      </div>

      {/* Sélecteurs de dates — libres, mais l'analyse se recale toujours sur l'ordre chronologique réel */}
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <div>
          <Select value={effDateA} onValueChange={setDateA}>
            <SelectTrigger
              className="bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_16px_-8px_rgba(217,70,239,0.3)] rounded-lg px-2.5 py-1.5 h-auto text-white text-xs focus:ring-primary focus:border-primary min-w-[110px]"
              data-testid="comparison-date-a"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border border-white/10 text-white">
              {dates.map((d) => <SelectItem key={d} value={d} disabled={d === effDateB} className="focus:bg-primary/20 focus:text-white">{fmtDate(d)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <ArrowRight className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
        <div>
          <Select value={effDateB} onValueChange={setDateB}>
            <SelectTrigger
              className="bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_16px_-8px_rgba(217,70,239,0.3)] rounded-lg px-2.5 py-1.5 h-auto text-white text-xs focus:ring-primary focus:border-primary min-w-[110px]"
              data-testid="comparison-date-b"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border border-white/10 text-white">
              {dates.map((d) => <SelectItem key={d} value={d} disabled={d === effDateA} className="focus:bg-primary/20 focus:text-white">{fmtDate(d)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-center gap-2 mb-4 flex-wrap text-[10px] font-bold uppercase tracking-wider">
        <span className={selAIsEarlier ? 'text-zinc-500' : 'text-primary'} data-testid="comparison-label-a">
          {selAIsEarlier ? (en ? 'Before' : 'Avant') : (en ? 'After' : 'Après')}
        </span>
        <span className="w-3.5 shrink-0" />
        <span className={selAIsEarlier ? 'text-primary' : 'text-zinc-500'} data-testid="comparison-label-b">
          {selAIsEarlier ? (en ? 'After' : 'Après') : (en ? 'Before' : 'Avant')}
        </span>
      </div>

      {/* Tableau des écarts par zone */}
      {rows.length > 0 ? (
        <div className="space-y-1.5 mb-5">
          {rows.map((z) => {
            const d = deltas[z.key];
            const improved = z.lowerBetter == null ? null : (z.lowerBetter ? d.abs < 0 : d.abs > 0);
            const neutral = Math.abs(d.abs) < 0.05;
            const color = neutral ? '#a1a1aa' : improved == null ? '#e4e4e7' : improved ? '#34D399' : '#fb7185';
            const Icon = neutral ? Minus : d.abs < 0 ? TrendingDown : TrendingUp;
            return (
              <div key={z.key} className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-white/5" data-testid={`comparison-row-${z.key}`}>
                <span className="text-xs text-zinc-400 w-32 shrink-0 truncate">{en ? z.en : z.fr}</span>
                <span className="text-xs text-zinc-300 tabular-nums">{d.a}{z.unit} <ArrowRight className="w-2.5 h-2.5 inline mx-1 text-zinc-600" /> {d.b}{z.unit}</span>
                <span className="text-xs font-bold flex items-center gap-1 tabular-nums" style={{ color }}>
                  <Icon className="w-3 h-3" /> {d.abs > 0 ? '+' : ''}{d.abs} {z.unit}
                  {d.pct != null && <span className="text-[10px] opacity-70">({d.pct > 0 ? '+' : ''}{d.pct}%)</span>}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-zinc-500 mb-4">{en ? 'No common measured zone between these 2 dates.' : 'Aucune zone mesurée en commun entre ces 2 dates.'}</p>
      )}

      {/* Analyse professionnelle */}
      <p className="text-xs font-bold tracking-wider text-primary mb-2">{en ? 'PROFESSIONAL ANALYSIS' : 'ANALYSE PROFESSIONNELLE'}</p>
      <div className="space-y-2" data-testid="comparison-insights">
        {insights.map((ins, i) => (
          <div
            key={i}
            className={`rounded-xl border flex items-start gap-2.5 ${TONE_BG[ins.tone]} ${ins.emphasis ? 'p-4' : 'p-2.5'}`}
            data-testid={`comparison-insight-${i}`}
          >
            <ins.icon className={`shrink-0 mt-0.5 ${ins.emphasis ? 'w-5 h-5' : 'w-3.5 h-3.5'}`} style={{ color: ins.color }} />
            <div>
              <p className={`font-bold text-white mb-0.5 ${ins.emphasis ? 'text-base' : 'text-xs'}`}>{ins.title}</p>
              <p className={`text-zinc-300 leading-relaxed ${ins.emphasis ? 'text-sm' : 'text-[11px]'}`}>{ins.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MeasurementComparison;
